<template>
  <div class="purchase-daily-report">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>采购日报</span>
        </div>
      </template>

      <!-- 查询表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="报表日期">
          <el-date-picker
            v-model="queryParams.reportDate"
            type="date"
            placeholder="选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 200px"
            @change="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 报表内容 -->
      <div v-loading="loading" class="report-content">
        <el-card v-if="reportData" shadow="never">
          <template #header>
            <div class="report-header">
              <span>采购日报 - {{ formatDate(reportData.reportDate) }}</span>
            </div>
          </template>

          <!-- 汇总信息 -->
          <el-row :gutter="16" class="summary-row">
            <el-col :span="8">
              <el-card class="summary-card">
                <div class="summary-label">总金额</div>
                <div class="summary-value amount">¥{{ formatAmount(reportData.totalAmount) }}</div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card class="summary-card">
                <div class="summary-label">采购单数</div>
                <div class="summary-value">{{ reportData.totalCount || 0 }}</div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card class="summary-card">
                <div class="summary-label">报表日期</div>
                <div class="summary-value">{{ formatDate(reportData.reportDate) }}</div>
              </el-card>
            </el-col>
          </el-row>

          <!-- 明细列表 -->
          <div v-if="reportData.items && reportData.items.length > 0" class="items-section">
            <h3>采购明细</h3>
            <el-table :data="reportData.items" border style="width: 100%">
              <el-table-column prop="purchaseOrderNumber" label="采购单号" width="150" />
              <el-table-column prop="supplierName" label="供应商" width="150" />
              <el-table-column prop="warehouseName" label="仓库" width="120" />
              <el-table-column prop="totalAmount" label="金额" width="120" align="right">
                <template #default="{ row }">
                  {{ row.totalAmount ? '¥' + formatAmount(row.totalAmount) : '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="purchaseStatus" label="状态" width="100">
                <template #default="{ row }">
                  <el-tag :type="getStatusType(row.purchaseStatus)">
                    {{ getStatusName(row.purchaseStatus) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="purchaseDate" label="采购日期" width="120">
                <template #default="{ row }">
                  {{ formatDate(row.purchaseDate) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
          <el-empty v-else description="暂无采购数据" :image-size="80" />
        </el-card>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { getPurchaseDailyReport } from '../api/accountant'

// 数据
const loading = ref(false)
const reportData = ref(null)
const queryParams = reactive({
  reportDate: null
})

// 方法
const formatDate = (date) => {
  if (!date) return '-'
  if (typeof date === 'string') {
    return date.substring(0, 10)
  }
  return date
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toFixed(2)
}

const getStatusName = (status) => {
  const statusMap = {
    'PENDING': '待采购',
    'PROCESSING': '采购中',
    'COMPLETED': '已完成',
    'CANCELLED': '已取消'
  }
  return statusMap[status] || status
}

const getStatusType = (status) => {
  const typeMap = {
    'PENDING': 'warning',
    'PROCESSING': 'info',
    'COMPLETED': 'success',
    'CANCELLED': 'danger'
  }
  return typeMap[status] || ''
}

const loadReport = async () => {
  loading.value = true
  try {
    const params = {}
    if (queryParams.reportDate) {
      params.reportDate = queryParams.reportDate
    }
    const res = await getPurchaseDailyReport(params)
    if (res.code === '000000') {
      reportData.value = res.data
    } else {
      ElMessage.error(res.msg || '获取采购日报失败')
    }
  } catch (error) {
    console.error('获取采购日报失败:', error)
    ElMessage.error('获取采购日报失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  loadReport()
}

const handleReset = () => {
  queryParams.reportDate = null
  loadReport()
}

// 生命周期
onMounted(() => {
  // 默认查询今天的报表
  const today = new Date()
  queryParams.reportDate = today.toISOString().substring(0, 10)
  loadReport()
})
</script>

<style scoped>
.purchase-daily-report {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
}

.search-form {
  margin-bottom: 20px;
}

.report-content {
  margin-top: 20px;
}

.report-header {
  font-weight: 600;
  font-size: 16px;
}

.summary-row {
  margin-bottom: 20px;
}

.summary-card {
  text-align: center;
}

.summary-label {
  color: #909399;
  font-size: 14px;
  margin-bottom: 10px;
}

.summary-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.summary-value.amount {
  color: #f56c6c;
}

.items-section {
  margin-top: 20px;
}

.items-section h3 {
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 600;
}
</style>

